import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { keyToActionMap } from "../contexts/GamepadProvider";
import {
  setHasConnectedMouseKeyboard,
  setInputMode,
} from "../store/slices/appState";
import { isDesktop } from "../constants/dimension";
import { isTV } from "../utils/platform";
import { getMenuGamepadId, isGamePlay } from "../models/location";

type InputMode = "mouse" | "gamepad";
type CrossInputProps = {
  setActiveElement: (element?: HTMLElement) => void;
  removeSavedElement: () => void;
};

export const setCrossMode = (mode: "mouse" | "gamepad") => {
  const currentMode = document.body.getAttribute("data-input-mode");
  if (currentMode !== mode) {
    document.body.setAttribute("data-input-mode", mode);
  }
};

export const isGamepadMode = () => {
  return document.body.getAttribute("data-input-mode") === "gamepad";
};

type ModeChangeCallback = (mode: string) => void;
const modeChangeListeners = new Set<ModeChangeCallback>();

export const onCrossModeChange = (cb: ModeChangeCallback): (() => void) => {
  modeChangeListeners.add(cb);
  return () => modeChangeListeners.delete(cb);
};

// Internal — called from the single observer
const notifyModeChange = (mode: string) => {
  modeChangeListeners.forEach((cb) => cb(mode));
};

export function useCrossInputListener({
  setActiveElement,
  removeSavedElement,
}: CrossInputProps) {
  const dispatch = useDispatch();

  useEffect(() => {
    let mouseX = 0;
    let mouseY = 0;
    // Track the last position where mouse mode was activated,
    let lastMoveX: number | null = null;
    let lastMoveY: number | null = null;
    const MOVE_THRESHOLD = 10;

    const onMouse = (e: MouseEvent) => {
      mouseX = e.clientX;
      mouseY = e.clientY;

      if (lastMoveX === null || lastMoveY === null) {
        lastMoveX = mouseX;
        lastMoveY = mouseY;
        return;
      }

      const dist = Math.hypot(mouseX - lastMoveX, mouseY - lastMoveY);
      if (dist < MOVE_THRESHOLD) return;

      lastMoveX = mouseX;
      lastMoveY = mouseY;
      setCrossMode("mouse");
      dispatch(setInputMode("mouse"));
      removeSavedElement();
      if (isDesktop) {
        dispatch(setHasConnectedMouseKeyboard(true));
      }
    };

    const onMouseDown = (e: MouseEvent) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      setCrossMode("mouse");
      dispatch(setInputMode("mouse"));
      if (isDesktop) {
        dispatch(setHasConnectedMouseKeyboard(true));
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (Object.keys(keyToActionMap).includes(e.key)) {
        setCrossMode("gamepad");
        if (isTV) {
          dispatch(setInputMode("remote"));
        } else {
          dispatch(setInputMode("mouse"));
        }
      }
    };

    const findNearestFocusable = (): HTMLElement | undefined => {
      if (isGamePlay()) return;

      const isDialogOpen = document.querySelector("dialog[open]");
      if (isDialogOpen) return;

      const elements = document.querySelectorAll<HTMLElement>(
        "[data-selected-class]",
      );
      let nearest: HTMLElement | undefined;
      let minDist = Infinity;
      elements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dist = Math.hypot(cx - mouseX, cy - mouseY);
        if (dist < minDist) {
          minDist = dist;
          nearest = el;
        }
      });

      if (nearest?.getAttribute("data-gamepad")?.startsWith("menu-")) {
        const currentActiveMenuElement = document.querySelector(
          `[data-gamepad="${getMenuGamepadId()}"]`,
        ) as HTMLElement;
        return currentActiveMenuElement || nearest;
      }
      return nearest;
    };

    window.addEventListener("mousemove", onMouse);
    window.addEventListener("mousedown", onMouseDown);
    window.addEventListener("keydown", onKeyDown);

    const observer = new MutationObserver(() => {
      const mode = document.body.getAttribute("data-input-mode") as InputMode;
      notifyModeChange(mode ?? "");

      if (mode !== "gamepad") {
        setActiveElement();
        document.body.style.cursor = "auto";
      } else {
        document.body.style.cursor = "none";
        // Inject a global style to hide cursor on ALL elements in gamepad mode
        let styleEl = document.head.querySelector("[data-cross-input]");
        if (!styleEl) {
          styleEl = document.createElement("style");
          styleEl.setAttribute("data-cross-input", "true");
          styleEl.textContent = `body[data-input-mode="gamepad"], body[data-input-mode="gamepad"] * { cursor: none !important; }`;
          document.head.appendChild(styleEl);
        }

        // Defer findNearestFocusable to avoid forced layout during style recalc
        requestAnimationFrame(() => {
          const nearest = findNearestFocusable();
          if (nearest) setActiveElement(nearest);
        });
      }
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["data-input-mode"],
    });

    return () => {
      window.removeEventListener("mousemove", onMouse);
      window.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("keydown", onKeyDown);
      observer.disconnect();
    };
  }, [setActiveElement, dispatch]);
}
